import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  MessageSquare,
  AlertTriangle,
  Ban,
  Users,
} from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { useGroupStore } from "@/stores/groupStore";
import { useLanguageStore } from "@/stores/languageStore";
import { cn } from "@/lib/cn";
import { chooseLocalizedText } from "@/lib/localizedText";
import {
  ModerationLevelSection,
  WarningLadderSection,
  ToggleSwitch,
  ToggleDot,
} from "@/pages/Groups/GroupSettingsSections";
import type { GroupSettings as GroupSettingsType } from "@/stores/groupStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const PAGE_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ...SPRING, staggerChildren: 0.05 },
  },
};

const SECTION_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

const COLLAPSE_VARIANTS = {
  hidden: { opacity: 0, height: 0, overflow: "hidden" as const },
  visible: {
    opacity: 1,
    height: "auto",
    overflow: "visible" as const,
    transition: { ...SPRING, opacity: { duration: 0.2 } },
  },
  exit: {
    opacity: 0,
    height: 0,
    overflow: "hidden" as const,
    transition: { duration: 0.2 },
  },
};

const MODERATION_FEATURES = [
  {
    key: "antiSpamEnabled" as const,
    icon: Shield,
    label: "Anti-Spam",
    description: "Block spam and promotional content",
  },
  {
    key: "antiFloodEnabled" as const,
    icon: MessageSquare,
    label: "Anti-Flood",
    description: "Limit message rate per user",
  },
  {
    key: "badWordsEnabled" as const,
    icon: Ban,
    label: "Bad Words",
    description: "Filter offensive language",
  },
  {
    key: "linkProtectionEnabled" as const,
    icon: Shield,
    label: "Link Protection",
    description: "Block suspicious links",
  },
];

function navigateBack() {
  window.location.hash = "/groups";
}

interface GroupSettingsPageProps {
  groupId: string;
}

export function GroupSettingsPage({ groupId }: GroupSettingsPageProps) {
  const language = useLanguageStore((state) => state.language);
  const [hasLoadedGroups, setHasLoadedGroups] = useState(false);
  const { selectedGroup, isLoading, fetchGroups, selectGroup, updateGroupSettings } =
    useGroupStore();

  useEffect(() => {
    setHasLoadedGroups(false);
    fetchGroups().finally(() => {
      selectGroup(groupId);
      setHasLoadedGroups(true);
    });
  }, [fetchGroups, groupId, selectGroup]);

  if (isLoading || (!hasLoadedGroups && !selectedGroup)) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Group Settings" onBack={navigateBack} />
        <main className="flex-1 px-4 pb-20 pt-2">
          <LoadingSkeleton variant="list" count={4} className="mt-2" />
        </main>
      </div>
    );
  }

  if (!selectedGroup) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Group not found / ቡድኑ አልተገኘም" onBack={navigateBack} />
        <main className="flex-1 px-4 pb-20 pt-6 text-sm text-[var(--color-text-muted)]">
          {chooseLocalizedText("This group is unavailable or was removed. / ይህ ቡድን የለም ወይም ተወግዷል።", language)}
        </main>
      </div>
    );
  }

  function handleToggle(key: keyof GroupSettingsType) {
    if (!selectedGroup) return;
    const current = selectedGroup.settings[key];
    if (typeof current === "boolean") {
      updateGroupSettings(groupId, { [key]: !current });
    }
  }

  function handleModerationLevel(level: GroupSettingsType["moderationLevel"]) {
    updateGroupSettings(groupId, { moderationLevel: level });
  }

  function handleWelcomeMessageChange(message: string) {
    updateGroupSettings(groupId, { welcomeMessage: message });
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title={selectedGroup.title} onBack={navigateBack} />

      <motion.main
        variants={PAGE_VARIANTS}
        initial="hidden"
        animate="visible"
        className="flex-1 px-4 pb-20 pt-2"
      >
        <OverviewSection
          memberCount={selectedGroup.memberCount}
          messagesModerated={selectedGroup.stats.messagesModerated}
          warningsIssued={selectedGroup.stats.warningsIssued}
          usersBanned={selectedGroup.stats.usersBanned}
        />

        <WelcomeMessageSection
          enabled={selectedGroup.settings.welcomeEnabled}
          message={selectedGroup.settings.welcomeMessage}
          groupTitle={selectedGroup.title}
          memberCount={selectedGroup.memberCount}
          onToggle={() => handleToggle("welcomeEnabled")}
          onMessageChange={handleWelcomeMessageChange}
        />

        <ContentModerationSection
          settings={selectedGroup.settings}
          onToggle={handleToggle}
        />

        <ModerationLevelSection
          selected={selectedGroup.settings.moderationLevel}
          onSelect={handleModerationLevel}
        />

        <WarningLadderSection />
      </motion.main>
    </div>
  );
}

function OverviewSection({
  memberCount,
  messagesModerated,
  warningsIssued,
  usersBanned,
}: {
  memberCount: number;
  messagesModerated: number;
  warningsIssued: number;
  usersBanned: number;
}) {
  const stats = [
    { icon: Users, value: memberCount, label: "Members", color: "text-blue-600" },
    { icon: Shield, value: messagesModerated, label: "Moderated", color: "text-emerald-600" },
    { icon: AlertTriangle, value: warningsIssued, label: "Warnings", color: "text-amber-600" },
    { icon: Ban, value: usersBanned, label: "Bans", color: "text-red-600" },
  ];

  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Group Overview
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className={cn(
                "flex flex-col items-center gap-1.5 rounded-[var(--radius-lg)] p-4",
                "bg-[var(--color-surface)] shadow-sm",
              )}
            >
              <Icon className={cn("h-5 w-5", stat.color)} />
              <span className="font-headline text-xl font-bold text-[var(--color-text)]">
                {stat.value}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {stat.label}
              </span>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

function WelcomeMessageSection({
  enabled,
  message,
  groupTitle,
  memberCount,
  onToggle,
  onMessageChange,
}: {
  enabled: boolean;
  message: string;
  groupTitle: string;
  memberCount: number;
  onToggle: () => void;
  onMessageChange: (message: string) => void;
}) {
  const [localMessage, setLocalMessage] = useState(message);

  useEffect(() => {
    setLocalMessage(message);
  }, [message]);

  function handleBlur() {
    if (localMessage !== message) {
      onMessageChange(localMessage);
    }
  }

  const previewText = localMessage
    .replace("{name}", "Abebe")
    .replace("{group}", groupTitle)
    .replace("{memberCount}", String(memberCount));

  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Welcome Message
      </h2>

      <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface)] shadow-sm p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              Greet New Members
            </h3>
            <p className="text-xs text-[var(--color-text-muted)]">
              {enabled ? "New members receive a welcome message" : "Welcome messages are off"}
            </p>
          </div>
          <ToggleSwitch
            enabled={enabled}
            onToggle={onToggle}
            ariaLabel="Toggle welcome message"
          />
        </div>

        <AnimatePresence>
          {enabled && (
            <motion.div
              key="welcome-editor"
              variants={COLLAPSE_VARIANTS}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <textarea
                value={localMessage}
                onChange={(e) => setLocalMessage(e.target.value)}
                onBlur={handleBlur}
                aria-label="Welcome message template"
                placeholder="Type your welcome message..."
                rows={3}
                className={cn(
                  "mb-2 w-full resize-none rounded-[var(--radius-md)]",
                  "shadow-sm bg-[var(--color-bg)] p-3",
                  "text-sm text-[var(--color-text)]",
                  "placeholder:text-[var(--color-text-muted)]",
                  "outline-none focus:ring-2 focus:ring-[var(--color-primary)]",
                )}
              />

              <p className="mb-3 text-[10px] text-[var(--color-text-muted)]">
                Available placeholders: {"{name}"}, {"{group}"}, {"{memberCount}"}
              </p>

              <div className="rounded-[var(--radius-md)] bg-[var(--color-bg)] p-3">
                <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
                  Preview
                </p>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {previewText}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
}

function ContentModerationSection({
  settings,
  onToggle,
}: {
  settings: GroupSettingsType;
  onToggle: (key: keyof GroupSettingsType) => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Content Moderation
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {MODERATION_FEATURES.map((feature) => {
          const isEnabled = settings[feature.key] as boolean;
          const Icon = feature.icon;
          return (
            <button
              key={feature.key}
              type="button"
              onClick={() => onToggle(feature.key)}
              aria-label={`Toggle ${feature.label}`}
              aria-pressed={isEnabled}
              className={cn(
                "flex flex-col items-start gap-2 rounded-[var(--radius-lg)] p-3.5",
                "text-left transition-all",
                isEnabled
                  ? "ring-2 ring-[var(--color-primary)] bg-[var(--color-primary)]/5"
                  : "shadow-sm bg-[var(--color-surface)]",
              )}
            >
              <div className="flex w-full items-center justify-between">
                <Icon
                  className={cn(
                    "h-5 w-5",
                    isEnabled
                      ? "text-[var(--color-primary)]"
                      : "text-[var(--color-text-muted)]",
                  )}
                />
                <ToggleDot enabled={isEnabled} />
              </div>
              <div>
                <p
                  className={cn(
                    "text-sm font-medium",
                    isEnabled
                      ? "text-[var(--color-text)]"
                      : "text-[var(--color-text-secondary)]",
                  )}
                >
                  {feature.label}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-[var(--color-text-muted)]">
                  {feature.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </motion.section>
  );
}
